import React, { useReducer, useMemo, useRef, useEffect, useState } from "react";
import { CustomButton, CustomTooltip, I18nTranslate, icon, IconComponent } from "../../../../../../common/components";
import { translate, translateElapsed } from "../../../../../../common/providers";
import { HISTORY_ITEM_TYPE } from "../../constants/constants";
import styles from "./HistoryRow.module.css";
import { translateFilterLabel, translateFilterOptionLabel } from "../../../../utils/translate-utils";
import { BOOLEAN_FILTER_LIST, CATEGORY_FILTER_LIST, DATE_FILTER_LIST } from "../../../../constants";
import { convertISO8601toDayFirstFormat } from "../../../../../../common/utils";

const HistoryRow = ({ history }) => {
	const [showAll, setShowAll] = useReducer((prev) => !prev, false);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const commentContentRef = useRef();
	const contentIdentifierIcon = useMemo(() => {
		switch (history.fieldChanged) {
			case HISTORY_ITEM_TYPE.STATUS:
				return icon.faCheck;
			case HISTORY_ITEM_TYPE.DETAILS:
				return icon.faComments;
			case HISTORY_ITEM_TYPE.THEME:
				return icon.faTag;
			case HISTORY_ITEM_TYPE.PRIORITY:
				return icon.faExclamationTriangle;
			case HISTORY_ITEM_TYPE.DEADLINE:
				return icon.faCalendar;
			case HISTORY_ITEM_TYPE.PILOT:
				return icon.faUser;
			case HISTORY_ITEM_TYPE.RISK:
				return icon.faExclamationTriangle;
			case HISTORY_ITEM_TYPE.FREQUENCY:
				return icon.faRotate;
			case HISTORY_ITEM_TYPE.CREATED:
				return icon.faFileSignature;
			case HISTORY_ITEM_TYPE.CONTENT_ADDED:
				return icon.faPlus;
			case HISTORY_ITEM_TYPE.CONTENT_REMOVED:
				return icon.faMinus;
			case HISTORY_ITEM_TYPE.COMMENT:
			case HISTORY_ITEM_TYPE.COMMENT_MODIFIED:
			case HISTORY_ITEM_TYPE.COMMENT_REMOVED:
				return icon.faComment;
			case HISTORY_ITEM_TYPE.DELETED:
			case HISTORY_ITEM_TYPE.BULK_DELETED:
				return icon.faTrash;
			default:
				return icon.faPen;
		}
	}, [history.fieldChanged]);

	const contentIdentifierText = useMemo(() => {
		switch (history.fieldChanged) {
			case HISTORY_ITEM_TYPE.CREATED:
				return "commitment-tracking.history.event.created";
			case HISTORY_ITEM_TYPE.CONTENT_ADDED:
				return "commitment-tracking.history.event.content-added";
			case HISTORY_ITEM_TYPE.CONTENT_REMOVED:
				return "commitment-tracking.history.event.content-removed";
			case HISTORY_ITEM_TYPE.COMMENT:
				return "commitment-tracking.history.event.comment";
			case HISTORY_ITEM_TYPE.COMMENT_MODIFIED:
				return "commitment-tracking.history.event.comment-modified";
			case HISTORY_ITEM_TYPE.COMMENT_REMOVED:
				return "commitment-tracking.history.event.comment-removed";
			case HISTORY_ITEM_TYPE.DELETED:
				return "commitment-tracking.history.event.deleted";
			case HISTORY_ITEM_TYPE.BULK_DELETED:
				return "commitment-tracking.history.event.bulk-deleted";
			default:
				return "commitment-tracking.history.event.field-edited";
		}
	}, [history.fieldChanged]);

	const handleIsOverFlowing = () => {
		setIsOverflowing(commentContentRef.current?.clientHeight < commentContentRef.current?.scrollHeight);
	};

	useEffect(() => {
		handleIsOverFlowing();
	}, []);

	useEffect(() => {
		window.addEventListener("resize", handleIsOverFlowing);
		return () => {
			window.removeEventListener("resize", handleIsOverFlowing);
		};
	}, []);

	const getContent = () => {
		const { fieldChanged, newContent, fieldName, comment } = history;
		switch (fieldChanged) {
			case HISTORY_ITEM_TYPE.CREATED:
				return null;
			case HISTORY_ITEM_TYPE.COMMENT:
			case HISTORY_ITEM_TYPE.COMMENT_MODIFIED:
			case HISTORY_ITEM_TYPE.COMMENT_REMOVED:
				return (
					<div className={styles.historyRow__commentContainer}>
						<div className={styles.historyRow__commentText}>{comment || newContent}</div>
					</div>
				);
			case HISTORY_ITEM_TYPE.CONTENT_ADDED:
			case HISTORY_ITEM_TYPE.CONTENT_REMOVED:
				return <div className={styles.historyRow__contentId}>{history.contentId || newContent}</div>;
			case HISTORY_ITEM_TYPE.DELETED:
			case HISTORY_ITEM_TYPE.BULK_DELETED:
				return null;
			case HISTORY_ITEM_TYPE.DETAILS:
				return (
					<div>
						<div ref={commentContentRef} className={showAll ? "" : styles.historyRow__commentContainer}>
							{(newContent || "").split("\n").map((com, idx) => (
								// eslint-disable-next-line react/no-array-index-key
								<p key={idx} className={styles.historyRow__commentLine}>
									{com}
								</p>
							))}
						</div>
						{isOverflowing && (
							<CustomButton className={styles.historyRow__showMoreBtn} size="sm" onClick={setShowAll}>
								{showAll ? translate("common:btn.show-less") : translate("common:btn.show-more")}
							</CustomButton>
						)}
					</div>
				);
			default:
				if (fieldName && newContent) {
					return (
						<div className={styles.historyRow__fieldChange}>
							<span className={styles.historyRow__fieldLabel}>{translateFilterLabel(fieldName)} : </span>
							<span className={styles.historyRow__fieldValue}>
								{(CATEGORY_FILTER_LIST.includes(fieldName.toLowerCase()) &&
									translateFilterOptionLabel(fieldName, newContent)) ||
									(BOOLEAN_FILTER_LIST.includes(fieldName.toLowerCase()) &&
										translate(`common:btn.${(newContent === "true" && "yes") || "no"}`)) ||
									(DATE_FILTER_LIST.includes(fieldName.toLowerCase()) &&
										convertISO8601toDayFirstFormat(newContent)) ||
									newContent}
							</span>
						</div>
					);
				}
				return newContent ? <div>{newContent}</div> : null;
		}
	};

	return (
		<div className={styles.historyRow__container}>
			<div className={styles.historyRow__header}>
				<div className={styles.historyRow__identifierContainer}>
					<IconComponent
						className={styles.historyRow__identifierIcon}
						color="var(--grey-600)"
						icon={contentIdentifierIcon}
						size="xs"
					/>
					<div className={styles.historyRow__identifier}>
						<span className={styles.historyRow__identifierText}>
							<I18nTranslate
								param={{
									user: history.username || translate("common:company-name.aitenders"),
									contentId: history.contentId || "",
									className: styles["historyRow__identifierText--companyName"],
								}}
								translationKey={contentIdentifierText}
							/>
						</span>
					</div>
				</div>
				<CustomTooltip title={history.date}>
					<div className={styles.historyRow__timePassed}>
						{translateElapsed({
							value: history.elapsed?.value || 0,
							unitKey: history.elapsed?.unit || "SECONDS",
						})}
					</div>
				</CustomTooltip>
			</div>
			<div>{getContent()}</div>
		</div>
	);
};

export default HistoryRow;
